import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const PortfolioCard = ({ company, onViewDetails }) => {
  const [isHovered, setIsHovered] = useState(false);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800 border-green-200';
      case 'Exited': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'IPO Ready': return 'bg-purple-100 text-purple-800 border-purple-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getGrowthColor = (growth) => {
    const numGrowth = parseFloat(growth?.replace('%', ''));
    if (numGrowth >= 50) return 'text-green-600';
    if (numGrowth >= 20) return 'text-blue-600';
    if (numGrowth >= 0) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div
      className="group bg-card border border-border rounded-brand shadow-brand hover-lift transition-brand overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Company Header */}
      <div className="relative h-48 overflow-hidden">
        <Image
          src={company?.image}
          alt={company?.imageAlt}
          className="w-full h-full object-cover group-hover:scale-105 transition-brand"
        />
        
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
        
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
          <div className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(company?.status)}`}>
            {company?.status}
          </div>
          
          <div className="flex items-center space-x-2">
            {company?.featured && (
              <div className="bg-yellow-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                <Icon name="Star" size={12} />
              </div>
            )}
            
            <div className="bg-white/90 backdrop-blur-sm px-2 py-1 rounded-full text-xs font-medium text-slate-800">
              {company?.industry}
            </div>
          </div>
        </div>

        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-white rounded-brand-sm p-2 shadow-brand-sm">
              <Image
                src={company?.logo}
                alt={company?.logoAlt}
                className="w-full h-full object-contain"
              />
            </div>
            
            <div>
              <h3 className="text-white font-bold text-lg">{company?.name}</h3>
              <p className="text-white/80 text-sm">{company?.location}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Company Details */}
      <div className="p-6">
        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">
          {company?.description}
        </p>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="text-center p-3 bg-muted/50 rounded-brand-sm">
            <div className="text-2xl font-bold text-foreground mb-1">{company?.valuation}</div>
            <div className="text-xs text-muted-foreground">Current Valuation</div>
          </div>
          
          <div className="text-center p-3 bg-muted/50 rounded-brand-sm">
            <div className={`text-2xl font-bold mb-1 ${getGrowthColor(company?.growth)}`}>
              {company?.growth}
            </div>
            <div className="text-xs text-muted-foreground">YoY Growth</div>
          </div>
        </div>

        {/* Investment Details */}
        <div className="space-y-3 mb-6">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Investment Stage:</span>
            <span className="font-medium text-foreground">{company?.stage}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Investment Date:</span>
            <span className="font-medium text-foreground">{company?.investmentDate}</span>
          </div>
          
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">REXFEL Stake:</span>
            <span className="font-medium text-foreground">{company?.stake}</span>
          </div>
        </div>

        {/* Key Achievements */}
        <div className="mb-6">
          <h4 className="text-sm font-medium text-foreground mb-3">Key Achievements</h4>
          <div className="space-y-2">
            {company?.achievements?.slice(0, 2)?.map((achievement, index) => (
              <div key={index} className="flex items-start space-x-2">
                <Icon name="CheckCircle" size={16} className="text-green-600 mt-0.5 flex-shrink-0" />
                <span className="text-sm text-muted-foreground">{achievement}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Founder Quote */}
        {company?.founderQuote && (
          <div className="bg-muted/30 rounded-brand-sm p-4 mb-6">
            <p className="text-sm text-muted-foreground italic mb-2">"{company?.founderQuote}"</p>
            <div className="flex items-center space-x-2">
              <Image
                src={company?.founderAvatar}
                alt={company?.founderAvatarAlt}
                className="w-6 h-6 rounded-full"
              />
              <span className="text-xs font-medium text-foreground">{company?.founderName}</span>
              <span className="text-xs text-muted-foreground">{company?.founderTitle}</span>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center space-x-3">
          <Button
            variant="default"
            size="sm"
            onClick={() => onViewDetails(company)}
            iconName="ArrowRight"
            iconPosition="right"
            className="flex-1"
          >
            View Case Study
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            iconName="ExternalLink"
            className="px-3"
          >
          </Button>
        </div>

        {/* Hover Overlay */}
        <div className={`absolute inset-0 bg-primary/5 transition-brand pointer-events-none ${
          isHovered ? 'opacity-100' : 'opacity-0'
        }`}></div>
      </div>
    </div>
  );
};

export default PortfolioCard;